'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Component, useState, type ReactNode } from 'react';
import { Toaster } from 'sonner';

import { MsalAuthProvider } from '@/lib/auth';
import { logger } from '@/lib/logger';

interface ProvidersProps {
  children: ReactNode;
}

// Error Boundary to catch and display React errors with more context
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo });
    // Log to console with full details
    logger.error('ErrorBoundary caught an error:', error);
    logger.error('Component stack:', errorInfo.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-red-50 p-8">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-2xl font-bold text-red-800 mb-4">
              Something went wrong
            </h1>
            <div className="bg-white rounded-lg shadow p-6 mb-4">
              <h2 className="font-semibold text-red-700 mb-2">Error Message:</h2>
              <pre className="bg-red-100 p-4 rounded text-sm overflow-auto">
                {this.state.error?.message}
              </pre>
            </div>
            {this.state.errorInfo && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="font-semibold text-red-700 mb-2">Component Stack:</h2>
                <pre className="bg-gray-100 p-4 rounded text-xs overflow-auto max-h-96">
                  {this.state.errorInfo.componentStack}
                </pre>
              </div>
            )}
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function Providers({ children }: ProvidersProps): JSX.Element {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  return (
    <ErrorBoundary>
      <MsalAuthProvider>
        <QueryClientProvider client={queryClient}>
          <>
            {children}
            <Toaster position="top-right" richColors />
            <ReactQueryDevtools initialIsOpen={false} />
          </>
        </QueryClientProvider>
      </MsalAuthProvider>
    </ErrorBoundary>
  );
}
